'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

/* ─── Hooks ──────────────────────────────────────────────────────────────── */

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.querySelectorAll<HTMLElement>('[data-reveal]').forEach((n) => n.classList.add('v2-in'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const delay = Number((e.target as HTMLElement).dataset.delay ?? 0);
            setTimeout(() => (e.target as HTMLElement).classList.add('v2-in'), delay);
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0 }
    );
    el.querySelectorAll('[data-reveal]').forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);
  return ref;
}

function useCountUp(target: number, opts: { suffix?: string; prefix?: string; decimals?: number; duration?: number } = {}) {
  const { suffix = '', prefix = '', decimals = 0, duration = 1800 } = opts;
  const fmt = (n: number) => (decimals > 0 ? n.toFixed(decimals) : String(Math.round(n)));
  const [display, setDisplay] = useState(`${prefix}${fmt(target)}${suffix}`);
  const ref = useRef<HTMLSpanElement>(null);
  const ran = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || ran.current) return;
      ran.current = true;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const start = performance.now();
      const step = (ts: number) => {
        const p = Math.min((ts - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(`${prefix}${fmt(eased * target)}${suffix}`);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix, prefix, decimals, duration]);
  return { display, ref };
}

/* ─── Reveal wrapper ─────────────────────────────────────────────────────── */

const R: React.FC<{ children: React.ReactNode; delay?: number; style?: React.CSSProperties; className?: string }> = ({
  children, delay = 0, style, className,
}) => (
  <div
    data-reveal
    data-delay={delay}
    className={className}
    style={style}
  >
    {children}
  </div>
);

/* ─── Shared tokens ──────────────────────────────────────────────────────── */
const C = {
  hero: '#030712',
  heroCard: '#0D1829',
  heroBorder: 'rgba(255,255,255,0.07)',
  blue: '#3B82F6',
  blueBright: '#60A5FA',
  blueDeep: '#1D4ED8',
  white: '#FFFFFF',
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate600: '#475569',
  slate700: '#334155',
  slate900: '#0F172A',
  green: '#16A34A',
  greenLight: '#DCFCE7',
  amber: '#F59E0B',
};

const font = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

/* ─── Keyframes injected once ────────────────────────────────────────────── */
const STYLE_ID = 'ifueltax-v2-styles';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

#v2-root { font-family: ${font}; }

[data-reveal] {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}
[data-reveal].v2-in {
  opacity: 1 !important;
  transform: none !important;
}

@keyframes v2-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
}
@keyframes v2-glow-pulse {
  0%, 100% { opacity: 0.4; }
  50%       { opacity: 0.8; }
}
@keyframes v2-ticker {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes v2-bar-grow {
  from { height: 0; }
}
@keyframes v2-dot-ping {
  0%   { transform: scale(1); opacity: 1; }
  75%, 100% { transform: scale(2.2); opacity: 0; }
}

.v2-ticker-wrap { overflow: hidden; }
.v2-ticker-track {
  display: flex;
  width: max-content;
  animation: v2-ticker 28s linear infinite;
}
.v2-ticker-track:hover { animation-play-state: paused; }

.v2-nav-link {
  color: rgba(15,23,42,0.6);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s;
}
.v2-nav-link:hover { color: #0F172A; }

.v2-btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  background: ${C.blue};
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px; font-weight: 700;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  cursor: pointer; border: none; outline: none;
  white-space: nowrap;
}
.v2-btn-primary:hover {
  background: ${C.blueDeep};
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(59,130,246,0.35);
}
.v2-btn-primary:active { transform: translateY(0); }

.v2-btn-ghost {
  display: inline-flex; align-items: center; gap: 8px;
  background: #fff;
  border: 1.5px solid #E2E8F0;
  color: #334155;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px; font-weight: 600;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
  cursor: pointer; outline: none;
  white-space: nowrap;
}
.v2-btn-ghost:hover {
  background: #F8FAFC;
  border-color: #CBD5E1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.v2-btn-ghost-dark {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,0.1);
  border: 1.5px solid rgba(255,255,255,0.2);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px; font-weight: 600;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s;
  cursor: pointer; outline: none;
  white-space: nowrap;
}
.v2-btn-ghost-dark:hover {
  background: rgba(255,255,255,0.18);
  border-color: rgba(255,255,255,0.35);
}

.v2-btn-outline {
  display: inline-flex; align-items: center; gap: 8px;
  background: transparent;
  border: 1.5px solid ${C.blue};
  color: ${C.blue};
  padding: 11px 24px;
  border-radius: 8px;
  font-size: 15px; font-weight: 700;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
  cursor: pointer; outline: none;
  white-space: nowrap;
}
.v2-btn-outline:hover {
  background: ${C.blue};
  color: #fff;
}

.v2-tab-btn {
  padding: 10px 22px;
  font-size: 14px; font-weight: 600;
  border-radius: 8px;
  border: none; cursor: pointer;
  transition: background 0.2s, color 0.2s;
  font-family: ${font};
}
.v2-tab-btn.active {
  background: ${C.blue};
  color: #fff;
}
.v2-tab-btn:not(.active) {
  background: transparent;
  color: ${C.slate600};
}
.v2-tab-btn:not(.active):hover {
  background: ${C.slate100};
}

.v2-faq-item {
  border-bottom: 1px solid ${C.slate200};
}
.v2-faq-item:first-child { border-top: 1px solid ${C.slate200}; }
.v2-faq-btn {
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  padding: 22px 0;
  background: none; border: none; cursor: pointer;
  font-family: ${font}; font-size: 16px; font-weight: 600;
  color: ${C.slate900}; text-align: left;
  gap: 16px;
}
.v2-faq-btn:hover { color: ${C.blue}; }
.v2-faq-chevron {
  flex-shrink: 0; width: 32px; height: 32px;
  border-radius: 50%; background: ${C.slate100};
  border: 1px solid ${C.slate200};
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s, border-color 0.2s, transform 0.3s;
  color: ${C.slate600};
}
.v2-faq-chevron.open {
  background: ${C.blue};
  border-color: ${C.blue};
  transform: rotate(180deg);
  color: #fff;
}
.v2-faq-body {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.4s ease, padding-bottom 0.4s ease;
  font-size: 15.5px; color: ${C.slate600}; line-height: 1.75;
  padding-bottom: 0;
}
.v2-faq-body.open { max-height: 600px; padding-bottom: 24px; }

.v2-card-hover {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.v2-card-hover:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.12) !important;
}

@media (max-width: 767px) {
  .v2-hero-grid { flex-direction: column !important; }
  .v2-mockup-wrap { display: none !important; }
  .v2-feat-grid { grid-template-columns: 1fr !important; }
  .v2-steps-grid { grid-template-columns: 1fr !important; }
  .v2-pricing-grid { grid-template-columns: 1fr !important; }
  .v2-test-grid { grid-template-columns: 1fr !important; }
  .v2-audience-grid { grid-template-columns: 1fr !important; }
  .v2-footer-grid { grid-template-columns: 1fr !important; }
  .v2-hero-title { font-size: clamp(32px, 8vw, 48px) !important; }
  .v2-section-title { font-size: clamp(26px, 6vw, 40px) !important; }
}
`;

function InjectStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => { document.getElementById(STYLE_ID)?.remove(); };
  }, []);
  return null;
}

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */
const Icon = {
  Arrow: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  Check: ({ color = C.green }: { color?: string }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Map: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" />
    </svg>
  ),
  CreditCard: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  FileText: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  Bell: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  Shield: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Users: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  BarChart: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  Link2: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Globe: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Star: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={C.amber} stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Truck: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  Briefcase: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Zap: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={C.white} stroke="none">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Quote: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(59,130,246,0.15)" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
    </svg>
  ),
};

/* ─── Dashboard Mockup ───────────────────────────────────────────────────── */
function DashboardMockup() {
  const BARS = [
    { h: 55, label: 'TX', color: C.blue },
    { h: 72, label: 'CA', color: C.blue },
    { h: 40, label: 'FL', color: 'rgba(59,130,246,0.5)' },
    { h: 65, label: 'IL', color: C.blue },
    { h: 50, label: 'OH', color: C.blue },
    { h: 83, label: 'NY', color: C.blueBright },
    { h: 45, label: 'PA', color: 'rgba(59,130,246,0.5)' },
  ];

  return (
    <div
      style={{
        background: '#0A1628',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 520,
        boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.08)',
        animation: 'v2-float 6s ease-in-out infinite',
        fontFamily: font,
      }}
    >
      {/* Title bar */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,255,255,0.03)',
      }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF5F57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#FEBC2E' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28C840' }} />
        <span style={{ marginLeft: 8, fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>iFuelTax — Q2 2025 Dashboard</span>
      </div>

      {/* Nav */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        {['Overview', 'Reports', 'States', 'Vehicles'].map((tab, i) => (
          <span key={tab} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: i === 0 ? C.blue : 'transparent',
            color: i === 0 ? '#fff' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
          }}>{tab}</span>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, position: 'relative' }}>
            <div style={{
              position: 'absolute', inset: -2, borderRadius: '50%',
              background: 'rgba(22,163,74,0.35)',
              animation: 'v2-dot-ping 2s ease-out infinite',
            }} />
          </div>
          <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Live</span>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { label: 'Total Miles', value: '284,391', unit: 'mi', delta: '+8.2%', up: true },
          { label: 'Fuel Purchased', value: '48,220', unit: 'gal', delta: '+4.1%', up: true },
          { label: 'Tax Owed', value: '$12,847', unit: '', delta: 'Q2 2025', up: null },
        ].map((s) => (
          <div key={s.label} style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: 4, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', lineHeight: 1, marginBottom: 3 }}>
              {s.value}<span style={{ fontSize: 10, fontWeight: 500, marginLeft: 3, color: 'rgba(255,255,255,0.4)' }}>{s.unit}</span>
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: s.up === true ? C.green : s.up === false ? '#EF4444' : 'rgba(255,255,255,0.35)' }}>
              {s.up === true ? '▲' : s.up === false ? '▼' : ''} {s.delta}
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Miles by State</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
          {BARS.map((b) => (
            <div key={b.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: '100%', height: `${b.h}%`, background: b.color, borderRadius: '3px 3px 0 0',
                animation: 'v2-bar-grow 0.8s ease-out',
              }} />
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{b.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* State table */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>State Filing Status</div>
        {[
          { state: 'Texas', miles: '82,440', tax: '$3,204', status: 'Filed', ok: true },
          { state: 'California', miles: '61,882', tax: '$2,890', status: 'Filed', ok: true },
          { state: 'Florida', miles: '48,220', tax: '$1,920', status: 'Pending', ok: false },
          { state: 'Illinois', miles: '38,770', tax: '$1,544', status: 'Filed', ok: true },
        ].map((row) => (
          <div key={row.state} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '7px 8px', borderRadius: 6, marginBottom: 2,
            background: 'rgba(255,255,255,0.02)',
            fontSize: 12,
          }}>
            <span style={{ color: '#fff', fontWeight: 600, width: 80 }}>{row.state}</span>
            <span style={{ color: 'rgba(255,255,255,0.45)', width: 70 }}>{row.miles} mi</span>
            <span style={{ color: 'rgba(255,255,255,0.45)', width: 55 }}>{row.tax}</span>
            <span style={{
              padding: '2px 9px', borderRadius: 100, fontSize: 10, fontWeight: 700,
              background: row.ok ? 'rgba(22,163,74,0.15)' : 'rgba(245,158,11,0.15)',
              color: row.ok ? C.green : C.amber,
            }}>{row.status}</span>
          </div>
        ))}
      </div>

      {/* Bottom action */}
      <div style={{ padding: '10px 16px 14px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 8,
          background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)',
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Q2 2025 Report Ready</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Due Jul 31 · 3 states pending</div>
          </div>
          <div style={{
            padding: '6px 14px', background: C.blue, borderRadius: 6,
            fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer',
          }}>File Now →</div>
        </div>
      </div>
    </div>
  );
}

/* Navbar consolidated — home now uses the shared <Header /> component
   (src/components/Header.jsx), same as every other page. */

/* ─── Hero ───────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{
      position: 'relative',
      background: '#F0F6FF',
      overflow: 'hidden',
      paddingTop: 140,
      paddingBottom: 100,
    }}>
      {/* Mesh gradient background */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 70% 60% at 50% -5%, rgba(59,130,246,0.14) 0%, transparent 65%),
          radial-gradient(ellipse 45% 40% at 85% 20%, rgba(99,102,241,0.07) 0%, transparent 60%),
          radial-gradient(ellipse 50% 60% at 0% 80%, rgba(59,130,246,0.06) 0%, transparent 60%)
        `,
      }} />

      {/* Grid overlay */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.035,
        backgroundImage: 'linear-gradient(rgba(15,23,42,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.4) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        <div className="v2-hero-grid" style={{ display: 'flex', alignItems: 'center', gap: 64 }}>
          {/* Left */}
          <div style={{ flex: '0 0 auto', maxWidth: 580 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.22)',
              borderRadius: 100, padding: '6px 14px', marginBottom: 28,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.blue, boxShadow: `0 0 8px ${C.blue}` }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.blueDeep, letterSpacing: '0.02em' }}>
                IFTA & Fuel Tax Compliance Platform
              </span>
            </div>

            <h1
              className="v2-hero-title"
              style={{
                fontSize: 'clamp(40px, 5vw, 64px)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.04em',
                color: C.slate900,
                marginBottom: 24,
                fontFamily: font,
              }}
            >
              Stop Doing IFTA{' '}
              <span style={{
                background: `linear-gradient(135deg, ${C.blue}, ${C.blueBright})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                By Hand
              </span>
            </h1>

            <p style={{
              fontSize: 18, lineHeight: 1.7, color: C.slate600,
              marginBottom: 36, maxWidth: 480, fontFamily: font,
            }}>
              Automate fuel tax compliance across the United States and Canada IFTA. Connect your GPS and fuel cards, generate quarterly reports in minutes, and file on time — every time.
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 36 }}>
              <a
                href="https://app.ifueltax.com/#/login?action_type=register"
                target="_blank" rel="noopener noreferrer"
                className="v2-btn-primary"
                style={{ fontSize: 16, padding: '14px 28px' }}
              >
                Start Free Trial <Icon.Arrow />
              </a>
              <Link href="/contact-us/" className="v2-btn-ghost" style={{ fontSize: 16, padding: '14px 28px' }}>
                Schedule a Demo
              </Link>

            </div>

            {/* Trust signals */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {[1,2,3,4,5].map((i) => <Icon.Star key={i} />)}
                <span style={{ fontSize: 13, color: C.slate600, marginLeft: 4, fontFamily: font }}>4.9 · 600+ fleets</span>
              </div>
              <div style={{ width: 1, height: 18, background: C.slate200 }} />
              <span style={{ fontSize: 13, color: C.slate600, fontFamily: font }}>30-day free trial · No credit card</span>
            </div>
          </div>

          {/* Right: Dashboard mockup */}
          <div className="v2-mockup-wrap" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <DashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trust Bar ──────────────────────────────────────────────────────────── */
const INTEGRATIONS = ['WEX Fuel Card', 'Comdata', 'Fleet One', 'Samsara', 'Motive (KeepTruckin)', 'Verizon Connect', 'ELD Mandate', 'IFTA Jurisdictions'];

function TrustBar() {
  return (
    <div style={{ background: '#fff', borderTop: `1px solid ${C.slate200}`, borderBottom: `1px solid ${C.slate200}`, padding: '18px 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.slate600, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', fontFamily: font }}>
            Integrates with
          </span>
          <div style={{ flex: 1, overflow: 'hidden' }} className="v2-ticker-wrap">
            <div className="v2-ticker-track">
              {[...INTEGRATIONS, ...INTEGRATIONS].map((name, i) => (
                <span key={i} style={{
                  padding: '6px 18px', margin: '0 4px',
                  background: C.slate50, border: `1px solid ${C.slate200}`,
                  borderRadius: 100, fontSize: 13, fontWeight: 600, color: C.slate700,
                  whiteSpace: 'nowrap', fontFamily: font,
                }}>{name}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stats ──────────────────────────────────────────────────────────────── */
function StatNum({ target, suffix = '', prefix = '', decimals = 0 }: { target: number; suffix?: string; prefix?: string; decimals?: number }) {
  const { display, ref } = useCountUp(target, { suffix, prefix, decimals });
  return <span ref={ref}>{display}</span>;
}

function Stats() {
  const ref = useReveal();
  return (
    <section style={{ background: '#fff', padding: '64px 0', borderBottom: `1px solid ${C.slate200}` }} ref={ref}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 32 }}>
          {[
            { target: 600, suffix: '+', label: 'Fleets Served', desc: 'and growing' },
            { target: 99.8, suffix: '%', decimals: 1, label: 'Accuracy Rate', desc: 'precise calculations' },
            { target: 58, suffix: '', label: 'States & Provinces', desc: 'US & Canada IFTA' },
            { target: 20, suffix: 'hrs', label: 'Saved Per Quarter', desc: 'vs. manual filing' },
          ].map((s, i) => (
            <R key={s.label} delay={i * 80}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.04em', color: C.slate900, lineHeight: 1, marginBottom: 6, fontFamily: font }}>
                  <StatNum target={s.target} suffix={s.suffix} prefix={''} decimals={s.decimals ?? 0} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.slate700, marginBottom: 3, fontFamily: font }}>{s.label}</div>
                <div style={{ fontSize: 13, color: C.slate600, fontFamily: font }}>{s.desc}</div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Audience Split ─────────────────────────────────────────────────────── */
function AudienceSection() {
  const ref = useReveal();
  return (
    <section style={{ background: C.slate50, padding: '96px 0' }} ref={ref}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <R style={{ textAlign: 'center', marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            Built For Two Types of Professionals
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, margin: 0, fontFamily: font }}
          >
            One Platform, Two Powerful Roles
          </h2>
        </R>

        <div className="v2-audience-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {[
            {
              icon: <Icon.Truck />,
              label: 'For Carriers',
              title: 'Run Your Fleet With Confidence',
              desc: 'iFuelTax connects directly to your GPS devices and fuel cards to automatically track mileage by jurisdiction. Generate your IFTA quarterly report in minutes — not days.',
              points: ['GPS-based mileage tracking (Samsara, Motive, Verizon)', 'Fuel card integration (WEX, Comdata, Fleet One)', 'IFTA quarterly report generation', 'Driver mobile app (iOS & Android)', 'Deadline reminders & record keeping'],
              cta: 'Start Carrier Trial',
              price: 'From $14.99/truck/month',
              delay: 0,
            },
            {
              icon: <Icon.Briefcase />,
              label: 'For Tax Agents',
              title: 'Scale Your IFTA Practice',
              desc: 'Manage unlimited carrier clients from a single dashboard. White-label reports, automated compliance alerts, and revenue tracking — built for the modern IFTA professional.',
              points: ['Unlimited carrier client accounts', 'White-label branded reports', 'Client self-service portal', 'Advanced analytics & dashboards', 'Expert IFTA consultation (2 hrs/mo)'],
              cta: 'Start Agent Trial',
              price: 'From $35/month',
              delay: 100,
            },
          ].map((card) => (
            <R key={card.label} delay={card.delay}>
              <div
                className="v2-card-hover"
                style={{
                  background: '#fff', border: `1px solid ${C.slate200}`,
                  borderRadius: 16, padding: '36px 36px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  display: 'flex', flexDirection: 'column', height: '100%',
                }}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: 12,
                  background: 'rgba(59,130,246,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20,
                }}>
                  {card.icon}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.blue, marginBottom: 8, display: 'block', fontFamily: font }}>
                  {card.label}
                </span>
                <h3 style={{ fontSize: 24, fontWeight: 800, color: C.slate900, marginBottom: 14, letterSpacing: '-0.02em', lineHeight: 1.2, fontFamily: font }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: 15, color: C.slate600, lineHeight: 1.7, marginBottom: 24, fontFamily: font }}>
                  {card.desc}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', flex: 1 }}>
                  {card.points.map((pt) => (
                    <li key={pt} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginTop: 1,
                      }}>
                        <Icon.Check />
                      </div>
                      <span style={{ fontSize: 14, color: C.slate700, lineHeight: 1.5, fontFamily: font }}>{pt}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <a
                    href="https://app.ifueltax.com/#/login?action_type=register"
                    target="_blank" rel="noopener noreferrer"
                    className="v2-btn-primary"
                  >
                    {card.cta} <Icon.Arrow />
                  </a>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.slate600, fontFamily: font }}>{card.price}</span>
                </div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Features ───────────────────────────────────────────────────────────── */
const FEATURES = {
  carriers: [
    { icon: <Icon.Map />, title: 'GPS Mileage Tracking', desc: 'Auto-sync miles by jurisdiction from Samsara, Motive, and 20+ ELD providers. No spreadsheets, no manual entry.' },
    { icon: <Icon.CreditCard />, title: 'Fuel Card Integration', desc: 'Pull fuel purchases directly from WEX, Comdata, and Fleet One. Gallons and costs sync instantly.' },
    { icon: <Icon.FileText />, title: 'One-Click IFTA Reports', desc: 'Generate quarterly IFTA reports in minutes. Export to PDF, CSV, or file directly.' },
    { icon: <Icon.Bell />, title: 'Deadline Reminders', desc: 'Never miss a Q1–Q4 filing deadline. Smart alerts keep your compliance status green all year.' },
    { icon: <Icon.Shield />, title: 'Compliance Records', desc: 'Every report backed by original GPS and fuel records. Full filing history stored and accessible anytime.' },
    { icon: <Icon.Globe />, title: 'US & Canada IFTA', desc: 'Full coverage across the United States and Canada. One login, complete North American IFTA compliance.' },
  ],
  agents: [
    { icon: <Icon.Users />, title: 'Unlimited Clients', desc: 'Manage hundreds of carrier clients from one dashboard. Add, remove, and monitor fleet accounts with zero extra cost.' },
    { icon: <Icon.FileText />, title: 'White-Label Reports', desc: 'Generate branded IFTA reports with your firm\'s logo and colors. Professional PDFs clients can trust.' },
    { icon: <Icon.BarChart />, title: 'Analytics Dashboard', desc: 'Real-time compliance scores, revenue tracking, and trend analysis across your entire client base.' },
    { icon: <Icon.Link2 />, title: 'Client Self-Service Portal', desc: 'Clients can upload their own fuel data, review reports, and track status — reducing your back-and-forth.' },
    { icon: <Icon.Bell />, title: 'Automated Alerts', desc: 'Proactive notifications for every client\'s filing deadlines. Never let a client miss a due date again.' },
    { icon: <Icon.Shield />, title: 'IFTA Expert Consultation', desc: '2 hours/month of direct consultation with certified IFTA professionals. Included with Agent plan.' },
  ],
};

function FeaturesSection() {
  const [tab, setTab] = useState<'carriers' | 'agents'>('carriers');
  const ref = useReveal();
  const gridRef = useRef<HTMLDivElement>(null);

  const switchTab = (t: 'carriers' | 'agents') => {
    setTab(t);
    // New items render without observer — force-reveal after paint
    setTimeout(() => {
      gridRef.current?.querySelectorAll<HTMLElement>('[data-reveal]').forEach((n, i) => {
        n.classList.remove('v2-in');
        setTimeout(() => n.classList.add('v2-in'), i * 55);
      });
    }, 20);
  };

  return (
    <section id="features" style={{ background: '#fff', padding: '96px 0' }} ref={ref}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <R style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            Platform Features
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, marginBottom: 16, fontFamily: font }}
          >
            Every Tool You Need to File Right
          </h2>
          <p style={{ fontSize: 17, color: C.slate600, maxWidth: 520, margin: '0 auto', lineHeight: 1.6, fontFamily: font }}>
            Built for the realities of fleet operations and tax compliance. No bloat, no learning curve.
          </p>
        </R>

        {/* Tabs */}
        <R delay={100}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
            <div style={{
              display: 'flex', background: C.slate100, borderRadius: 10, padding: 4, gap: 2,
            }}>
              {(['carriers', 'agents'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => switchTab(t)}
                  className={`v2-tab-btn${tab === t ? ' active' : ''}`}
                >
                  {t === 'carriers' ? 'For Carriers' : 'For Agents'}
                </button>
              ))}
            </div>
          </div>
        </R>

        <div ref={gridRef} className="v2-feat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {FEATURES[tab].map((f, i) => (
            <R key={f.title} delay={i * 60}>
              <div
                className="v2-card-hover"
                style={{
                  padding: '28px', borderRadius: 14,
                  border: `1px solid ${C.slate200}`, background: '#fff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{
                  width: 46, height: 46, borderRadius: 10,
                  background: 'rgba(59,130,246,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  {f.icon}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.slate900, marginBottom: 8, letterSpacing: '-0.01em', fontFamily: font }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: C.slate600, lineHeight: 1.65, margin: 0, fontFamily: font }}>{f.desc}</p>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ───────────────────────────────────────────────────────── */
function HowItWorks() {
  const ref = useReveal();
  return (
    <section style={{ background: C.slate50, padding: '96px 0' }} ref={ref}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <R style={{ textAlign: 'center', marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            How It Works
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, margin: 0, fontFamily: font }}
          >
            Set Up in Under 10 Minutes
          </h2>
        </R>

        <div className="v2-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32 }}>
          {[
            {
              step: '01',
              title: 'Connect Your Tools',
              desc: 'Link your GPS provider and fuel cards in one click. iFuelTax pulls mileage and fuel data automatically — no CSV uploads, no manual entry.',
              icon: <Icon.Link2 />,
            },
            {
              step: '02',
              title: 'Track Automatically',
              desc: 'Your dashboard stays up to date every day. Miles by jurisdiction, fuel by state, tax owed — live and accurate across every truck in your fleet.',
              icon: <Icon.BarChart />,
            },
            {
              step: '03',
              title: 'File With Confidence',
              desc: 'When the quarter ends, your IFTA report is ready. Review the numbers, approve, and file — or let iFuelTax send it directly to the jurisdiction.',
              icon: <Icon.FileText />,
            },
          ].map((s, i) => (
            <R key={s.step} delay={i * 100}>
              <div style={{ position: 'relative' }}>
                {/* Connector line (desktop only) */}
                {i < 2 && (
                  <div aria-hidden="true" style={{
                    position: 'absolute', top: 36, left: 'calc(100% - 16px)', width: 32, height: 2,
                    background: `linear-gradient(90deg, ${C.blue}, ${C.slate200})`,
                    zIndex: 1,
                    display: 'none',
                  }} className="v2-connector" />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 14,
                      background: `linear-gradient(135deg, ${C.blue}, ${C.blueDeep})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                      flexShrink: 0,
                    }}>
                      {React.cloneElement(s.icon as React.ReactElement<any>, { style: { color: '#fff' } })}
                    </div>
                    <span style={{ fontSize: 48, fontWeight: 900, color: C.slate200, letterSpacing: '-0.05em', lineHeight: 1, fontFamily: font }}>{s.step}</span>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: C.slate900, marginBottom: 10, letterSpacing: '-0.02em', fontFamily: font }}>{s.title}</h3>
                    <p style={{ fontSize: 15, color: C.slate600, lineHeight: 1.7, margin: 0, fontFamily: font }}>{s.desc}</p>
                  </div>
                </div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing ────────────────────────────────────────────────────────────── */
function Pricing() {
  const [annual, setAnnual] = useState(true);
  const ref = useReveal();

  const plans = [
    {
      name: 'Solo',
      tagline: 'For owner-operators running a single truck',
      price: annual ? '$12.49' : '$14.99',
      period: annual ? 'per truck/mo, billed $149.88/yr' : 'per truck / month',
      savings: annual ? 'Save 17%' : null,
      features: [
        'Trip entry & tracking',
        'Fuel entry by state',
        'Mileage tracking by state',
        'IFTA quarterly report generation',
        'Trip stops & route logging',
        'Truck & trailer management',
        'Record keeping & filing history',
        'Report history & PDF export',
        'Email support',
      ],
      cta: 'Start Free Trial',
      featured: false,
    },
    {
      name: 'Agent Reporting',
      tagline: 'For tax professionals managing multiple carriers',
      price: annual ? '$30' : '$35',
      period: annual ? 'per month, billed $360/yr' : 'per month',
      savings: annual ? 'Save $60/yr' : null,
      features: [
        'Everything in Solo',
        'Up to 5 trucks',
        'Multiple company profiles',
        'IFTA quarterly reports',
        'IRP mileage reports',
        'Weight distance reports (OR, KY, NM, NY)',
        'Report history & PDF export',
        'Multi-driver management',
        'Trailer tracking',
        'Priority email support',
      ],
      cta: 'Start Free Trial',
      featured: true,
    },
    {
      name: 'Routing & Reporting',
      tagline: 'For fleets needing full routing and compliance reporting',
      price: annual ? '$53.99' : '$59.99',
      period: annual ? 'per month, billed $647.88/yr' : 'per month',
      savings: annual ? 'Save $72/yr' : null,
      features: [
        'Everything in Agent Reporting',
        'Unlimited trucks',
        'GPS stop & route tracking',
        'Advanced trip routing tools',
        'Full state compliance reports (IFTA, IRP, OR, KY, NM, NY)',
        'Mileage rate management',
        'Accounting & transaction tracking',
        'Advanced analytics & graphs',
        'Priority phone & email support',
      ],
      cta: 'Start Free Trial',
      featured: false,
    },
  ];

  return (
    <section id="pricing" style={{ background: C.slate50, padding: '96px 0', position: 'relative', overflow: 'hidden' }} ref={ref}>
      {/* Subtle blue tint top */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 320,
        background: 'linear-gradient(180deg, rgba(59,130,246,0.05) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            Pricing
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, marginBottom: 12, fontFamily: font }}
          >
            Simple, Transparent Pricing
          </h2>
          <p style={{ fontSize: 17, color: C.slate600, fontFamily: font }}>
            No setup fees. No long-term contracts. 30-day free trial.
          </p>

          {/* Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 28 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: annual ? C.slate600 : C.slate900, fontFamily: font }}>Monthly</span>
            <button
              onClick={() => setAnnual((v) => !v)}
              aria-checked={annual}
              role="switch"
              aria-label="Toggle annual billing"
              style={{
                width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                background: annual ? C.blue : C.slate200,
                position: 'relative', transition: 'background 0.25s',
                flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: annual ? 27 : 3,
                width: 22, height: 22, background: '#fff', borderRadius: '50%',
                transition: 'left 0.25s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: annual ? C.slate900 : C.slate600, fontFamily: font }}>Annual</span>
            <span style={{
              background: annual ? C.greenLight : C.slate100,
              border: annual ? `1px solid rgba(22,163,74,0.3)` : `1px solid ${C.slate200}`,
              color: annual ? C.green : C.slate600,
              padding: '3px 12px', borderRadius: 100, fontSize: 12, fontWeight: 700, fontFamily: font,
              transition: 'all 0.25s',
            }}>Save up to 17%</span>
          </div>
        </div>

        <div className="v2-pricing-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, maxWidth: 1100, margin: '0 auto' }}>
          {plans.map((plan, i) => (
            <R key={plan.name} delay={i * 100}>
              <div
                className="v2-card-hover"
                style={{
                  position: 'relative',
                  background: plan.featured ? `linear-gradient(145deg, ${C.blueDeep}, #1e40af)` : '#fff',
                  border: plan.featured ? `1px solid ${C.blueDeep}` : `1px solid ${C.slate200}`,
                  borderRadius: 16, padding: '28px 24px',
                  boxShadow: plan.featured ? `0 8px 40px rgba(29,78,216,0.25)` : '0 2px 12px rgba(0,0,0,0.05)',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                {plan.featured && (
                  <div style={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: `linear-gradient(90deg, ${C.blue}, ${C.blueDeep})`,
                    color: '#fff', padding: '5px 18px', borderRadius: 100,
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', fontFamily: font,
                  }}>
                    <Icon.Zap /> Most Popular
                  </div>
                )}
                <h3 style={{ fontSize: 20, fontWeight: 800, color: plan.featured ? '#fff' : C.slate900, marginBottom: 6, fontFamily: font }}>{plan.name}</h3>
                <p style={{ fontSize: 13, color: plan.featured ? 'rgba(255,255,255,0.7)' : C.slate600, marginBottom: 20, lineHeight: 1.5, fontFamily: font }}>{plan.tagline}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 38, fontWeight: 900, color: plan.featured ? '#fff' : C.slate900, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: font }}>{plan.price}</span>
                  <span style={{ fontSize: 13, color: plan.featured ? 'rgba(255,255,255,0.55)' : C.slate600, lineHeight: 1.4, fontFamily: font }}>{plan.period}</span>
                </div>
                {plan.savings && (
                  <span style={{ fontSize: 12, color: plan.featured ? '#86efac' : C.green, fontWeight: 700, marginBottom: 24, display: 'block', fontFamily: font }}>{plan.savings}</span>
                )}
                <div style={{ height: plan.savings ? 0 : 24 }} />
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', flex: 1 }}>
                  {plan.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 11 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: plan.featured ? 'rgba(134,239,172,0.2)' : C.greenLight,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginTop: 1,
                      }}>
                        <Icon.Check color={plan.featured ? '#86efac' : C.green} />
                      </div>
                      <span style={{ fontSize: 14, color: plan.featured ? 'rgba(255,255,255,0.8)' : C.slate700, lineHeight: 1.5, fontFamily: font }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://app.ifueltax.com/#/login?action_type=register"
                  target="_blank" rel="noopener noreferrer"
                  className={plan.featured ? 'v2-btn-primary' : 'v2-btn-ghost'}
                  style={{ justifyContent: 'center', textAlign: 'center' }}
                >
                  {plan.cta} <Icon.Arrow />
                </a>
              </div>
            </R>
          ))}
        </div>

        {/* Guarantee */}
        <R delay={200} style={{ textAlign: 'center', marginTop: 36 }}>
          <span style={{ fontSize: 14, color: C.slate600, fontFamily: font }}>
            No setup fees &nbsp;·&nbsp; Cancel anytime
          </span>
        </R>
      </div>
    </section>
  );
}

/* ─── Testimonials ───────────────────────────────────────────────────────── */
function Testimonials() {
  const ref = useReveal();
  const TESTS = [
    {
      name: 'Marcus T.',
      role: 'Owner-Operator, 12 trucks',
      location: 'Dallas, TX',
      quote: 'Before iFuelTax, IFTA filing took me two full days every quarter. Now it takes 20 minutes. The GPS sync with Samsara is seamless — I haven\'t manually entered a mile in over a year.',
      initials: 'MT',
    },
    {
      name: 'Sandra R.',
      role: 'IFTA Tax Agent, 80+ clients',
      location: 'Nashville, TN',
      quote: 'I doubled my client roster without hiring anyone. The white-label reports look professional and my clients love the self-service portal. It\'s the backbone of my practice now.',
      initials: 'SR',
    },
    {
      name: 'Derek W.',
      role: 'Fleet Manager, 47 units',
      location: 'Phoenix, AZ',
      quote: 'Switched from doing everything manually in Excel. iFuelTax connects straight to our GPS and fuel cards — mileage and purchases just sync automatically. Quarterly filing used to take us two full days. Now it\'s done in 20 minutes.',
      initials: 'DW',
    },
  ];

  return (
    <section style={{ background: C.slate50, padding: '96px 0' }} ref={ref}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <R style={{ textAlign: 'center', marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            Testimonials
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, margin: 0, fontFamily: font }}
          >
            Trusted by Fleets Across North America
          </h2>
        </R>

        <div className="v2-test-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {TESTS.map((t, i) => (
            <R key={t.name} delay={i * 80}>
              <div
                className="v2-card-hover"
                style={{
                  background: '#fff', border: `1px solid ${C.slate200}`,
                  borderRadius: 16, padding: '32px',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
                  display: 'flex', flexDirection: 'column', gap: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {[1,2,3,4,5].map((s) => <Icon.Star key={s} />)}
                </div>
                <div>
                  <Icon.Quote />
                </div>
                <p style={{ fontSize: 15, color: C.slate700, lineHeight: 1.75, margin: 0, fontFamily: font, flex: 1 }}>
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 16, borderTop: `1px solid ${C.slate100}` }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${C.blue}, ${C.blueDeep})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: font, flexShrink: 0,
                  }}>{t.initials}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.slate900, fontFamily: font }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: C.slate600, fontFamily: font }}>{t.role} · {t.location}</div>
                  </div>
                </div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ────────────────────────────────────────────────────────────────── */
const FAQS = [
  { q: 'What is IFTA and who needs to file?', a: 'IFTA (International Fuel Tax Agreement) is a compact among 48 US states and 10 Canadian provinces to simplify fuel tax reporting for carriers that operate across jurisdictions. Any carrier with a qualified motor vehicle (2+ axles over 26,000 lbs, or 3+ axles regardless of weight) operating in 2+ IFTA jurisdictions must file quarterly reports.' },
  { q: 'How does iFuelTax connect to my GPS provider?', a: 'iFuelTax integrates directly with Samsara, Motive (KeepTruckin), Verizon Connect, and 15+ other ELD providers via API. Once you authorize the connection, mileage by jurisdiction syncs automatically — no manual data entry needed.' },
  { q: 'Can I try iFuelTax before paying?', a: 'Yes. Every plan starts with a 30-day free trial — no credit card required. You\'ll have access to all features, including GPS sync and report generation. If you decide not to continue, you simply stop. No charges, no strings.' },
  { q: 'How does the Agent plan handle multiple clients?', a: 'Agent accounts can add unlimited carrier clients. Each client gets their own sub-account with isolated data. You manage everything from your main dashboard — view all clients, generate reports, monitor compliance status, and handle billing from one place.' },
  { q: 'Is my data secure?', a: 'Yes. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). iFuelTax is hosted on SOC 2 compliant infrastructure. We never sell your data to third parties, and you can export or delete your data at any time.' },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const ref = useReveal();

  return (
    <section id="faq" style={{ background: '#fff', padding: '96px 0' }} ref={ref}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px' }}>
        <R style={{ textAlign: 'center', marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: 12, fontFamily: font }}>
            FAQ
          </span>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', color: C.slate900, margin: 0, fontFamily: font }}
          >
            Common Questions
          </h2>
        </R>

        <R delay={80}>
          <div>
            {FAQS.map((faq, i) => (
              <div key={i} className="v2-faq-item">
                <button
                  className="v2-faq-btn"
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                >
                  <span style={{ fontFamily: font }}>{faq.q}</span>
                  <span className={`v2-faq-chevron${open === i ? ' open' : ''}`} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                <div className={`v2-faq-body${open === i ? ' open' : ''}`} style={{ fontFamily: font }}>
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </R>
      </div>
    </section>
  );
}

/* ─── CTA ────────────────────────────────────────────────────────────────── */
function FinalCTA() {
  const ref = useReveal();
  return (
    <section style={{ background: `linear-gradient(135deg, ${C.blueDeep} 0%, #1e40af 50%, #1d4ed8 100%)`, padding: '100px 0', position: 'relative', overflow: 'hidden' }} ref={ref}>
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.06,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }} />
      <div aria-hidden="true" style={{
        position: 'absolute', top: '-30%', left: '50%', transform: 'translateX(-50%)',
        width: 700, height: 500, borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.08), transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <R>
          <h2
            className="v2-section-title"
            style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, letterSpacing: '-0.04em', color: '#fff', marginBottom: 20, fontFamily: font }}
          >
            Your Next IFTA Filing Doesn't Have to Be a Headache
          </h2>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, marginBottom: 40, fontFamily: font }}>
            Join 600+ fleets who automated their fuel tax compliance. Set up in 10 minutes. File your first report in the same quarter.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.ifueltax.com/#/login?action_type=register"
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#fff', color: C.blueDeep,
                padding: '16px 32px', borderRadius: 8,
                fontSize: 16, fontWeight: 800, textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                cursor: 'pointer', fontFamily: font,
                transition: 'transform 0.15s, box-shadow 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.22)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.transform = ''; (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)'; }}
            >
              Start Free 30-Day Trial <Icon.Arrow />
            </a>
            <Link href="/contact-us/" className="v2-btn-ghost-dark" style={{ fontSize: 16, padding: '16px 32px' }}>
              Talk to Sales
            </Link>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 24, fontFamily: font }}>
            No credit card required &nbsp;·&nbsp; Cancel anytime
          </p>
        </R>
      </div>
    </section>
  );
}

/* ─── Footer ─────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={{ background: '#020917', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '60px 0 40px', fontFamily: font }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div className="v2-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 40, marginBottom: 48 }}>
          {/* Brand */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <img
                src="/images/ifueltax-logo.png"
                alt="iFuelTax"
                width={110}
                height={34}
                style={{ height: 34, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, maxWidth: 280, marginBottom: 20 }}>
              IFTA & fuel tax compliance automation for carriers and agents across North America.
            </p>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
              © {new Date().getFullYear()} iFuelTax. All rights reserved.
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>Product</h4>
            {[['Features', '#features'], ['Pricing', '#pricing'], ['IFTA Software', '/ifta-software/'], ['IFTA & IRP Tax Software', '/ifta-irp-tax-software/'], ['Software for Service Providers', '/ifta-software-for-service-providers/'], ['Weight-Distance Tax Software', '/weight-distance-tax-software/'], ['Free IFTA Calculator', '/ifta-mileage-tax-calculator/'], ['Samsara IFTA Integration', '/integrations/samsara-ifta/'], ['IFTA by State', '/ifta/']].map(([label, href]) => (
              <a key={label} href={href} style={{ display: 'block', fontSize: 14, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
              >{label}</a>
            ))}
          </div>

          {/* Resources */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>Resources</h4>
            {[['Blog', '/blog/'], ['FAQ', '#faq'], ['IFTA Due Dates', '/blog/ifta-reporting-periods-and-due-dates/'], ['How to Calculate IFTA', '/blog/how-to-calculate-ifta-tax-step-by-step/'], ['IFTA Audit Guide', '/resources/ifta-audit-survival-guide/'], ['IFTA vs IRP vs Weight-Distance', '/resources/ifta-irp-weight-distance-guide/'], ['Kentucky KYU Tax', '/kentucky-weight-distance-tax-report/'], ['New Mexico WDT', '/new-mexico-weight-distance-tax-report/'], ['Support', '/support/'], ['Contact', '/contact-us/']].map(([label, href]) => (
              <a key={label} href={href} style={{ display: 'block', fontSize: 14, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
              >{label}</a>
            ))}
          </div>

          {/* Legal */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>Legal</h4>
            {[['Privacy Policy', '/privacy-policy/'], ['Terms of Service', '/terms-of-service/']].map(([label, href]) => (
              <a key={label} href={href} style={{ display: 'block', fontSize: 14, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
              >{label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Root ───────────────────────────────────────────────────────────────── */
export default function LandingV2() {
  return (
    <div id="v2-root" style={{ fontFamily: font }}>
      <InjectStyles />
      <Header />
      <Hero />
      <TrustBar />
      <Stats />
      <AudienceSection />
      <FeaturesSection />
      <HowItWorks />
      <Pricing />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
